import type { GupAuthMethod, GupRedirectParams } from '../api/backend/gup';
import type { UserProfileType } from '../api/backend/types';

const visitKeys = ['active', 'callbackUrl', 'authMethod', 'source', 'lang', 'srv', 'crNumber', 'processInstanceId'];
export type GupVisit = Omit<GupRedirectParams, 'serviceId' | 'crNumber' | 'authMethod'> & {
  authMethod?: GupAuthMethod;
  srv: string;
  crNumber?: string;
  processInstanceId?: string;
};
const gupAuthMethods: readonly string[] = ['theqa', 'mpki'] satisfies GupAuthMethod[];
const isGupAuthMethod = (value: string): value is GupAuthMethod => gupAuthMethods.includes(value);

// Which services an individual may start is decided by the backend at process start, not here.
export type GupBlock = 'unknownCr';

export const resolveGupService = (serviceId?: string | null): string | undefined => serviceId?.trim() || undefined;

export const getPortalRedirect = (target?: string): string | undefined => {
  if (!target || !target.startsWith('/') || target.startsWith('//')) return;
  const url = new URL(target, window.location.origin);
  if (url.origin !== window.location.origin) return;
  return url.pathname + url.search + url.hash;
};

// Read ciphertext as an opaque value; URLSearchParams would turn literal Base64 '+' into spaces.
export const getGupLanding = (target?: string): { landingUrl: string; encRequest: string } | undefined => {
  if (!target || !target.startsWith('/') || target.startsWith('//')) return;
  const url = new URL(target, window.location.origin);
  if (url.origin !== window.location.origin || !/^\/(?:start-service\/?|processes\/[^/]+\/?)$/.test(url.pathname))
    return;
  const raw = url.search
    .slice(1)
    .split('&')
    .find((part) => part.split('=')[0] === 'encRequest');
  if (raw === undefined) return;
  try {
    return { landingUrl: url.pathname + url.search, encRequest: decodeURIComponent(raw.slice(raw.indexOf('=') + 1)) };
  } catch {
    return;
  }
};

let visitGeneration = 0;
export const getGupVisitGeneration = () => visitGeneration;

const clearGupStorage = () => {
  for (const key of [...visitKeys, 'encRequest', 'landingUrl']) sessionStorage.removeItem(`gup.${key}`);
};

export const clearGupVisit = () => {
  visitGeneration += 1;
  clearGupStorage();
};

const isStoredGupLanding = (landing: { landingUrl: string; encRequest: string }) =>
  sessionStorage.getItem('gup.encRequest') === landing.encRequest &&
  sessionStorage.getItem('gup.landingUrl') === landing.landingUrl;

// A Gov.om link this tab has not captured yet; a link coming back from our own login round trip is not new.
export const isNewGupLanding = (target?: string) => {
  const landing = getGupLanding(target);
  return !!landing && !isStoredGupLanding(landing);
};

export const captureGupLanding = (target?: string) => {
  const landing = getGupLanding(target);
  if (!landing) return;
  if (!isStoredGupLanding(landing)) {
    clearGupVisit();
    clearGupSsoFailed();
    sessionStorage.setItem('gup.encRequest', landing.encRequest);
    sessionStorage.setItem('gup.landingUrl', landing.landingUrl);
  }
  return landing;
};

const ssoFailedKey = 'gup.ssoFailed';
const ssoStartedKey = 'gup.ssoStarted';

export const isGupSsoFailed = () => sessionStorage.getItem(ssoFailedKey) === 'true';

export const markGupSsoFailed = () => {
  sessionStorage.removeItem(ssoStartedKey);
  sessionStorage.setItem(ssoFailedKey, 'true');
};

export const clearGupSsoFailed = () => {
  sessionStorage.removeItem(ssoStartedKey);
  sessionStorage.removeItem(ssoFailedKey);
};

// Set before leaving for THEQA; still set on /login means the SSO round trip never came back (e.g. browser back).
export const isGupSsoStarted = () => sessionStorage.getItem(ssoStartedKey) === 'true';

export const markGupSsoStarted = () => sessionStorage.setItem(ssoStartedKey, 'true');

export const clearGupSsoStarted = () => sessionStorage.removeItem(ssoStartedKey);

// Identity comes from Gov.om, so a new Gov.om link ends any existing OPAZ session first. Set before the Keycloak
// logout; the login page reads it once to continue with the stored landing.
const sessionResetKey = 'gup.sessionReset';

export const markGupSessionReset = () => sessionStorage.setItem(sessionResetKey, 'true');

export const consumeGupSessionReset = () => {
  const reset = sessionStorage.getItem(sessionResetKey) === 'true';
  sessionStorage.removeItem(sessionResetKey);
  return reset;
};

// QA switch for test portals without THEQA access: sign in on the portal instead. Ignored on any other host.
const theqaSkipHosts = ['dev-portal.opaz.me', 'stg-portal.opaz.me'];

export const isGupTheqaSkipped = () =>
  theqaSkipHosts.includes(window.location.hostname) && localStorage.getItem('gup.skipTheqa') === 'true';

export const getPendingGupLanding = () =>
  getGupLanding(sessionStorage.getItem('gup.landingUrl') ?? undefined)?.landingUrl;

export const activateGupVisit = (params: GupRedirectParams, processInstanceId?: string): GupVisit => {
  // Details links carry serviceId null; the application id comes from the route instead.
  const srv = resolveGupService(params.serviceId);
  if (
    (!srv && !processInstanceId) ||
    (params.authMethod != null && !isGupAuthMethod(params.authMethod)) ||
    !['en', 'ar'].includes(params.lang) ||
    !['GUP', 'ANDROID', 'IPHONE', 'HUAWEI'].includes(params.source)
  )
    throw new Error('Unsupported GUP parameters');
  const callback = new URL(params.callbackUrl);
  if (callback.protocol !== 'https:') throw new Error('Invalid GUP callback');
  const crNumber = params.crNumber?.trim();
  const visit: GupVisit = {
    callbackUrl: params.callbackUrl,
    ...(params.authMethod ? { authMethod: params.authMethod } : {}),
    source: params.source,
    lang: params.lang,
    srv: srv ?? '',
    ...(crNumber && crNumber !== '0' ? { crNumber } : {}),
    ...(processInstanceId ? { processInstanceId } : {}),
  };
  clearGupStorage();
  for (const [key, value] of Object.entries(visit)) sessionStorage.setItem(`gup.${key}`, value);
  sessionStorage.setItem('gup.active', 'true');
  return visit;
};

export const getGupVisit = (processInstanceId?: string): GupVisit | undefined => {
  if (sessionStorage.getItem('gup.active') !== 'true') return;
  const values = Object.fromEntries(visitKeys.map((key) => [key, sessionStorage.getItem(`gup.${key}`)]));
  if (
    !values.callbackUrl ||
    (values.authMethod && !isGupAuthMethod(values.authMethod)) ||
    (!values.srv && !values.processInstanceId) ||
    !values.lang ||
    !values.source ||
    (processInstanceId && values.processInstanceId !== processInstanceId)
  )
    return;
  return {
    callbackUrl: values.callbackUrl,
    ...(values.authMethod ? { authMethod: values.authMethod as GupAuthMethod } : {}),
    srv: values.srv ?? '',
    lang: values.lang as GupVisit['lang'],
    source: values.source as GupVisit['source'],
    ...(values.crNumber ? { crNumber: values.crNumber } : {}),
    ...(values.processInstanceId ? { processInstanceId: values.processInstanceId } : {}),
  };
};

const gupSources = ['GUP', 'ANDROID', 'IPHONE', 'HUAWEI'] as const;

export const captureGupReviewLink = (
  search: {
    callbackUrl?: string;
    authMethod?: string;
    lang?: string;
    source?: string;
    crNumber?: string;
  },
  processInstanceId: string,
): GupVisit | undefined => {
  const authMethod = search.authMethod?.toLowerCase();
  if (!search.callbackUrl || (authMethod && !isGupAuthMethod(authMethod))) return;
  let callback: URL;
  try {
    callback = new URL(search.callbackUrl);
  } catch {
    return;
  }
  if (callback.protocol !== 'https:' || !(callback.hostname === 'gov.om' || callback.hostname.endsWith('.gov.om')))
    return;
  const source = (search.source || 'GUP').toUpperCase();
  if (!gupSources.includes(source as (typeof gupSources)[number])) return;
  const lang = search.lang === 'ar' ? 'ar' : 'en';
  const crNumber = search.crNumber?.trim();
  clearGupStorage();
  const stored: Record<string, string> = {
    active: 'true',
    callbackUrl: search.callbackUrl,
    source,
    lang,
    processInstanceId,
  };
  if (authMethod) stored.authMethod = authMethod;
  if (crNumber && crNumber !== '0') stored.crNumber = crNumber;
  for (const [key, value] of Object.entries(stored)) sessionStorage.setItem(`gup.${key}`, value);
  return getGupVisit(processInstanceId);
};

export const bindGupProcess = (processInstanceId: string) => {
  if (getGupVisit()) {
    sessionStorage.setItem('gup.processInstanceId', processInstanceId);
  }
};

// The OPAZ profile that mirrors the Gov.om one (BRD 2.10): the company of the CR Gov.om sent, or the individual
// profile when Gov.om sends no CR.
export const getGupProfile = (visit: GupVisit, user: UserProfileType) => {
  if (visit.crNumber)
    return user.profiles.find(
      (profile) =>
        profile.type === 'COMPANY' &&
        'companyDetails' in profile &&
        (profile.companyDetails.crNumber === visit.crNumber || profile.identifier === visit.crNumber),
    );
  return user.profiles.find((profile) => profile.type === 'INVESTOR');
};

export const getGupStartBlock = (visit: GupVisit, user: UserProfileType): GupBlock | undefined => {
  if (visit.crNumber && !getGupProfile(visit, user)) return 'unknownCr';
};

// BRD 2.10: use the exact callbackUrl and append authMethod and crNumber. URL.searchParams would re-encode the
// callback's own query (e.g. redirect=en/w/x becomes en%2Fw%2Fx), so the parameters are appended as text.
export const buildGupReturnUrl = (visit: GupVisit): string => {
  const extra = new URLSearchParams();
  if (visit.authMethod) extra.set('authMethod', visit.authMethod);
  if (visit.crNumber) extra.set('crNumber', visit.crNumber);
  const query = extra.toString();
  if (!query) return visit.callbackUrl;
  const hashAt = visit.callbackUrl.indexOf('#');
  const base = hashAt === -1 ? visit.callbackUrl : visit.callbackUrl.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : visit.callbackUrl.slice(hashAt);
  const separator = !base.includes('?') ? '?' : /[?&]$/.test(base) ? '' : '&';
  return `${base}${separator}${query}${hash}`;
};

export const buildGupDashboardUrl = (visit: GupVisit): string => {
  const url = new URL(`/${visit.lang}/sign-in`, new URL(visit.callbackUrl).origin);
  url.searchParams.set('redirect', 'dashboard');
  if (visit.authMethod) url.searchParams.set('authMethod', visit.authMethod);
  url.searchParams.set('source', visit.source);
  return url.href;
};

export const returnToGup = (visit: GupVisit, dashboard = false) => {
  const url = dashboard ? buildGupDashboardUrl(visit) : buildGupReturnUrl(visit);
  clearGupVisit();
  window.location.assign(url);
};

export const isGupLeaveOutcome = (result: string) => ['discard', 'back'].includes(result.toLowerCase());

export const clearGupVisitOutsideProcess = (pathname: string) => {
  const visit = getGupVisit();
  if (!visit) {
    const pending = getPendingGupLanding();
    if (
      pending &&
      ![
        '/login',
        '/signup',
        '/verify-email',
        new URL(pending, window.location.origin).pathname.replace(/\/$/, ''),
      ].includes(pathname.replace(/\/$/, '') || '/')
    )
      clearGupVisit();
    return;
  }
  const path = pathname.replace(/\/$/, '');
  const processPath = `/processes/${visit.processInstanceId}`;
  if (
    visit.processInstanceId ? path !== processPath && !path.startsWith(processPath + '/') : path !== '/start-service'
  ) {
    clearGupVisit();
  }
};
